import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import {
  combineLatest as observableCombineLatest,
  Observable,
  Subscription,
} from "rxjs";
import { metricsState } from "../../store/metrics/state";
import { KpiService } from "../../kpi/kpi.service";
import { DashboardDisplayService } from "../../dashboardDisplay/dashboardDisplay.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Title } from "@angular/platform-browser";
import { Store } from "@ngrx/store";
import { forEach, isEqual, sortBy } from "lodash";
import {
  GET_SCHOOLS,
  UpdateSelectedSchoolLevelAction,
} from "../../store/schools/actions";
import {
  GetMetricsAction,
  GetSelectedMetricDataAction,
  SAVE_METRIC_TITLE_DESC,
  SAVE_OTHER_REPORTS_URL,
  SelectMetricAction,
  UpdateMetricDescAction,
  UpdateMetricNameAction,
  UpdateMetricVisibilityAction,
  UpdateotherReportsUrlAction,
} from "../../store/metrics/actions";
import { GET_DISTRICT } from "../../store/district/actions";
import {
  getDistrictState,
  getGoalsState,
  getMetrics,
  getMetricsState,
  getSchoolsState,
} from "../../store/appState";
import {
  ApplyFilterGoalAction,
  GetGoalsAction,
  saveGoalsAction,
  UpdateFilterGoalInactiveAction,
  UpdateFilterGoalValueAction,
  UpdateGoalAction,
  UpdateGoalInactiveAction,
  updateMissingSchoolGoalsAction,
} from "../../store/goals/actions";

@Component({
  selector: "metric-settings-editor",
  templateUrl: "./metric-settings-editor.component.html",
  styleUrls: ["./metric-settings-editor.component.scss"],
})
export class MetricSettingsEditorComponent implements OnInit, OnDestroy {
  @ViewChild(MetricSettingsEditorComponent)
  metricSettingsBody: MetricSettingsEditorComponent;
  kpiId;
  metricsForCustomer;

  schoolsAndGoalsAndDistrictObs;
  metricObjs;
  hasDataWall;

  goals: Observable<any>;
  schools: Observable<any>;
  metrics: metricsState;
  district: Observable<any>;
  getMetricsSub: Subscription;
  getMetricsStateSub: Subscription;
  isStarter: boolean;

  constructor(
    private kpiService: KpiService,
    private dashboardDisplayService: DashboardDisplayService,
    private route: ActivatedRoute,
    private titleService: Title,
    private router: Router,
    private store: Store<any>
  ) {}

  ngOnInit() {
    this.kpiId = this.route.snapshot.paramMap.get("metricId");
    this.store.dispatch({
      type: GET_SCHOOLS,
    });
    this.store.dispatch(new GetMetricsAction(this.kpiId));
    this.store.dispatch({
      type: GET_DISTRICT,
    });
    this.goals = this.store.select(getGoalsState);
    this.schools = this.store.select(getSchoolsState);
    this.district = this.store.select(getDistrictState);
    this.getMetricsStateSub = this.store
      .select(getMetricsState)
      .subscribe((metricsState) => {
        this.metrics = metricsState;
      });
    this.getMetricsSub = this.store.select(getMetrics).subscribe((metrics) => {
      if (metrics && metrics.length > 0 && !isEqual(metrics, this.metricObjs)) {
        this.metricObjs = metrics;
        // dispatch an action to get the metric data and goals
        if (this.kpiId == null) {
          this.kpiId = sortBy(metrics, ["long_name"])[0].kpi_id;
        }
        this.dispatchSelectedMetricActions(this.kpiId);
      }
    });
    this.handleNoSchoolGoalObj();

    // get Data Wall subscription info
    this.kpiService.getUserInfo().subscribe(
      (data) => {
        this.hasDataWall =
          (data.has_datawall && data.has_datawall == "true") ||
          (data.has_demodatawall && data.has_demodatawall == "true");
        this.isStarter = data.sz_tier == "starter";
      },
      (error) => {
        console.error(error);
      }
    );
  }

  ngOnDestroy(): void {
    this.getMetricsStateSub.unsubscribe();
    this.getMetricsSub.unsubscribe();
    this.schoolsAndGoalsAndDistrictObs.unsubscribe();
  }

  setTitle(newTitle: string) {
    this.titleService.setTitle(newTitle);
  }

  updateMetric() {
    this.store.dispatch({
      type: SAVE_METRIC_TITLE_DESC,
    });
  }

  dispatchSelectedMetricActions(kpiId) {
    this.store.dispatch(new SelectMetricAction(kpiId));
    this.store.dispatch(new GetGoalsAction(kpiId));
    this.store.dispatch(new GetSelectedMetricDataAction(kpiId));
  }

  selectNewMetricForCustomer(metric) {
    this.kpiId = metric.kpi_id;
    this.dispatchSelectedMetricActions(this.kpiId);
    this.router.navigate(["/metric/settings/editor/", this.kpiId]);
  }

  metricNameUpdated(updatedName) {
    this.store.dispatch(new UpdateMetricNameAction(updatedName));
  }

  metricDescUpdated(updatedDesc) {
    this.store.dispatch(new UpdateMetricDescAction(updatedDesc));
  }

  goalValueChanged(value) {
    this.store.dispatch(new UpdateGoalAction(value));
  }

  goalInactiveChecked(value) {
    this.store.dispatch(new UpdateGoalInactiveAction(value));
  }

  filterGoalValueChanged(value) {
    this.store.dispatch(new UpdateFilterGoalValueAction(value));
  }

  filterGoalInactiveChanged(value) {
    this.store.dispatch(new UpdateFilterGoalInactiveAction(value));
  }

  applyFilterGoal(filterGoalsObj) {
    this.store.dispatch(new ApplyFilterGoalAction(filterGoalsObj));
  }

  schoolLevelSelected(event) {
    this.store.dispatch(new UpdateSelectedSchoolLevelAction(event.value));
  }

  saveGoalsClicked() {
    this.store.dispatch(new saveGoalsAction(this.kpiId));
  }

  otherReportsUrlChanged(event) {
    this.store.dispatch(new UpdateotherReportsUrlAction(event));
  }

  saveOtherReportsUrl() {
    this.store.dispatch({
      type: SAVE_OTHER_REPORTS_URL,
    });
  }

  handleNoSchoolGoalObj() {
    this.schoolsAndGoalsAndDistrictObs = observableCombineLatest(
      this.store.select(getSchoolsState),
      this.store.select(getGoalsState),
      this.store.select(getDistrictState),
      (schools, goals, district) => {
        if (
          schools &&
          goals &&
          district &&
          schools.pending == false &&
          goals.pending == false &&
          district.pending == false
        ) {
          return { schools, goals, district };
        } else {
          return null;
        }
      }
    ).subscribe((data: any) => {
      if (data) {
        let missingSchoolGoals = [];
        //dispatch an action if there are schools which are not found in goals to add blank goal objects to the original and updated data
        forEach(data.schools.data.schools, (school) => {
          if (!data.goals.originalData[school.systemSchoolId]) {
            missingSchoolGoals.push(school.systemSchoolId);
          }
        });
        if (missingSchoolGoals.length > 0) {
          this.store.dispatch(
            new updateMissingSchoolGoalsAction({
              schoolIds: missingSchoolGoals,
              districtId: data.district.data.id,
            })
          );
        }
        this.schoolsAndGoalsAndDistrictObs.unsubscribe();
      }
    });
  }
}
